import type { PageTitleSink } from "@seagames/ui";
import type { LinkProps } from "@tanstack/react-router";
import { type ReactNode, useId, useLayoutEffect, useState, useSyncExternalStore } from "react";

/**
 * Page-bar API — what the shell's top bar (components/shell/top-bar.tsx)
 * shows for the current page. The designer's rule: title, subtitle, back
 * arrow and page help live in the top bar; pages draw NO big in-page <h1> and
 * NO "← Back to …" link of their own.
 *
 * Every field is optional. Without a call the bar derives its heading from
 * the nav tree (components/sidebar/nav.ts → `pageHeadingFor`): the route's nav
 * label as the title, its section as the subtitle ("View Records /
 * Accreditation"; the Dashboard is subtitled with the Games' name).
 *
 *   // a record page: the person's name, a back arrow, a help button
 *   usePageBar({
 *     title: person.full_name,
 *     subtitle: t("detail.subtitle"),             // e.g. "Accreditation record"
 *     back: { to: "/accreditation/records", label: t("detail.backToRecords") },
 *     actions: (
 *       <PageBarButton icon={<InfoIcon />} label={t("help.howItWorks")} onClick={openHelp} />
 *     ),
 *   });
 *
 * - Call it once, in the route's page component (top level, like any hook).
 *   It is cleaned up when the page unmounts.
 * - `title` / `subtitle` are strings or nodes, already translated.
 * - `back` is a typed router link (`to` + optional `params` / `search`); its
 *   `label` is the arrow's accessible name (default "Back"). On phones the
 *   arrow takes the hamburger's place, as in the prototype.
 * - `actions` sit at the right of the bar, before the language button and
 *   the Games logo. Use `PageBarButton` (below): a 40px outlined button with
 *   icon + label on desktop, a 44px icon-only button on phones. Anything else
 *   you pass must fit a 3.5rem-tall phone bar.
 * - Nested routes may both call it: per field, the deeper (later-rendered)
 *   page wins and `undefined` inherits.
 *
 * A `<ListHeader>` from @seagames/ui at h1 level hands its title here too
 * (`pageTitleSink`), so list pages need no call at all; an explicit `title`
 * from `usePageBar` beats it.
 */
export interface PageBarBack {
	to: LinkProps["to"];
	params?: LinkProps["params"];
	search?: LinkProps["search"];
	/** Accessible name of the arrow. Default: "Back". */
	label?: string;
}

export interface PageBarOptions {
	title?: ReactNode;
	subtitle?: ReactNode;
	back?: PageBarBack;
	actions?: ReactNode;
}

type Entry = { seq: number; opts: PageBarOptions };

/*
 * A tiny external store rather than context state: pages WRITE it and only
 * the top bar READS it, so a page's update re-renders the bar and never the
 * page itself (state in the shell would re-render the whole outlet, which
 * would write again — a loop).
 */
const entries = new Map<string, Entry>();
const hoistedTitles = new Map<string, ReactNode>();
const listeners = new Set<() => void>();
let snapshot: PageBarOptions = {};
let seqCounter = 0;

const KEYS = ["title", "subtitle", "back", "actions"] as const;

function publish() {
	const next: PageBarOptions = {};
	const ordered = [...entries.values()].sort((a, b) => a.seq - b.seq);
	for (const { opts } of ordered)
		for (const k of KEYS) if (opts[k] !== undefined) (next as Record<string, unknown>)[k] = opts[k];
	if (next.title === undefined && hoistedTitles.size > 0) next.title = [...hoistedTitles.values()].at(-1);
	snapshot = next;
	for (const l of listeners) l();
}

function subscribe(cb: () => void) {
	listeners.add(cb);
	return () => {
		listeners.delete(cb);
	};
}

/** The resolved options for the current page (the top bar's read side). */
export function usePageBarState(): PageBarOptions {
	return useSyncExternalStore(subscribe, () => snapshot, () => snapshot);
}

/** Set what the shell's top bar shows while this page is mounted. See the module comment. */
export function usePageBar(opts: PageBarOptions): void {
	const id = useId();
	// Render order is parent-first, so a nested page gets the higher number and wins.
	const [seq] = useState(() => ++seqCounter);
	useLayoutEffect(() => {
		entries.set(id, { seq, opts });
		publish();
	});
	useLayoutEffect(
		() => () => {
			entries.delete(id);
			publish();
		},
		[id],
	);
}

/** Handed to @seagames/ui's `PageTitleProvider` by the shell: `<ListHeader>` titles land here. */
export const pageTitleSink: PageTitleSink = {
	set: (id, title) => {
		if (hoistedTitles.get(id) === title) return;
		hoistedTitles.set(id, title);
		publish();
	},
	clear: (id) => {
		if (hoistedTitles.delete(id)) publish();
	},
};

/**
 * A top-bar action — her `.thd__help` ("How it works"): 40px, white, a
 * --line-2 border, navy 13.5px semibold label with a 16px icon. Below `lg`
 * it is her 44px icon-only `.top__b`, the label kept as its accessible name.
 */
export function PageBarButton({
	icon,
	label,
	onClick,
	disabled,
}: {
	icon: ReactNode;
	label: string;
	onClick: () => void;
	disabled?: boolean;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			title={label}
			className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-[0.5rem] rounded-[0.5rem] text-gms-navy-ink outline-none transition-colors hover:bg-base-200 focus-visible:ring-2 focus-visible:ring-gms-navy/40 disabled:cursor-not-allowed disabled:opacity-40 max-lg:size-[2.75rem] lg:h-[2.5rem] lg:border lg:border-gms-line-2 lg:bg-base-100 lg:px-[0.875rem] lg:text-[0.84375rem] lg:font-semibold lg:text-gms-navy lg:hover:border-gms-navy lg:hover:bg-base-100 [&>svg]:size-[1.25rem] lg:[&>svg]:size-[1rem]"
		>
			{icon}
			<span className="max-lg:sr-only">{label}</span>
		</button>
	);
}
